import type { AssistantPageContext } from '@contracts/assistant';
import { currentQuarter, quarterOfMonth } from '@domain/dates';

/**
 * Spec §8.2, plan P3 D162–D166: the starter chips of an empty conversation, by page. A chip is a prompt, sent as the
 * person's own message with the page context; each one is answerable with the P3 capabilities (Task 6's reads).
 */
export const DASHBOARD_CHIPS: readonly string[] = ['Hôm nay có gì cần tôi xử lý?', 'Việc quá hạn của cả team',
  'Tóm tắt tuần này'];
const PROJECT_CHIPS = ['Dự án này đang ở đâu?', 'Việc nào đang chặn?', 'Giao việc trong dự án này'];
const STRATEGY_CHIPS = ['Mục tiêu nào đang chậm?', 'Việc nào đang phục vụ ưu tiên quý này?',
  'Tạo việc cho ưu tiên quý này'];
const PARTNER_CHIPS = ['Mình đang làm gì với đối tác này?', 'Giao việc liên quan tới đối tác này'];
/** D162: the Workplace chips P3 answers; they stay. */
const CARD_CHIPS = ['Việc này đang thế nào?', 'Dời hạn việc này'];
const TREE_CHIP = 'Tóm tắt cây này';
const OVERDUE_CHIP = 'Việc nào đang quá hạn?';
/**
 * D209, spec §8.2's Workplace row verbatim: the structural chips P4's capabilities answer. The three about a card show
 * only while one is picked, and go with it as the page's object; the other two need none.
 */
const STRUCT_CARD = ['Phân rã', 'Di chuyển nhánh', 'Chốt xong'];
const STRUCT_ALWAYS = ['Dựng cây', 'Đổi hạn hàng loạt'];
/** D164: Quản trị is guidance only. */
const ADMIN_CHIPS = ['Hướng dẫn tôi dùng trang này'];

const under = (pathname: string, prefix: string): boolean => pathname === prefix || pathname.startsWith(`${prefix}/`);

/** D163: the quarter of the month the Tài chính page shows, else of the business date. */
function quarterFor(page: AssistantPageContext | null, now: Date): number {
  const month = page?.facts?.kind === 'finance' ? page.facts.month : undefined;
  return month === undefined ? currentQuarter(now).quarter : quarterOfMonth(Number(month.slice(5, 7)));
}

export function chipsFor(page: AssistantPageContext | null, pathname: string, personaName: string, now: Date):
  readonly string[] {
  if (pathname === '/workplace') {
    const tree = page?.facts?.kind === 'workplace' && page.facts.treeId !== undefined;
    const card = page?.objectRef !== undefined;
    return [...(card ? CARD_CHIPS : []), ...(tree ? [TREE_CHIP] : []), OVERDUE_CHIP, ...(card ? STRUCT_CARD : []),
      ...STRUCT_ALWAYS];
  }
  if (under(pathname, '/projects') && page?.objectRef?.type === 'project') return PROJECT_CHIPS;
  if (under(pathname, '/partners') && page?.objectRef?.type === 'partner') return PARTNER_CHIPS;
  if (under(pathname, '/strategy')) return STRATEGY_CHIPS;
  // D165: every finance page, the settings and the finance project page included.
  if (under(pathname, '/finance')) return ['Vì sao chi tháng này tăng?', `So với kế hoạch Q${quarterFor(page, now)}`];
  if (under(pathname, '/activity')) return ['Hôm qua ai đã thay đổi gì?', `Những gì ${personaName} đã làm tuần này`];
  if (under(pathname, '/admin')) return ADMIN_CHIPS;
  return DASHBOARD_CHIPS;
}
